import { AddOutlined } from '@mui/icons-material';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import { Button, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { Suspense } from 'react';

import { useDeleteAssessment } from '@/api';
import { DeletePopup, Loader, PageHeaderLayout } from '@/shared/components';
import { CustomIconSwitch } from '@/shared/components/switch';
import { globalEnums } from '@/shared/duck';
import { useMedia } from '@/shared/hooks';
import { useAssessmentsContext } from '@/shared/providers';

import { AssessmentAdminModal, CreateEditModal, ScoreUpdateModal } from './components';

const LazyAssessmentsTableView = React.lazy(() => import('./components/table-view'));
const LazyAssessmentsRibbonView = React.lazy(() => import('./components/ribbon-view'));

const AssessmentsPage: React.FC = () => {
  const { isSxScreen } = useMedia();
  const { viewType, setViewType, selectedAssessment, setSelectedAssessment } = useAssessmentsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { deleteAssessment, isDeleting } = useDeleteAssessment();
  const [openCreateEditModal, setOpenCreateEditModal] = React.useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [openAdminToolsModal, setOpenAdminToolsModal] = React.useState(false);
  const [openScoreUpdateModal, setOpenScoreUpdateModal] = React.useState(false);
  const [isCreateMode, setIsCreateMode] = React.useState(false);

  const handleToggleView = () => {
    const newViewType =
      viewType === globalEnums.EAssessmentsViewType.RIBBON
        ? globalEnums.EAssessmentsViewType.TABLE
        : globalEnums.EAssessmentsViewType.RIBBON;
    setViewType(newViewType);
  };

  const handleDeleteAssessment = async () => {
    try {
      if (selectedAssessment) {
        await deleteAssessment(selectedAssessment.id);
        enqueueSnackbar({
          message: 'Assessment deleted successfully!',
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar({
        message: error instanceof Error ? error.message : 'An error occurred while deleting the organization.',
        variant: 'error',
      });
    }

    setSelectedAssessment(undefined);
  };

  const handleCreateAssessment = () => {
    setIsCreateMode(true);
  };

  React.useEffect(() => {
    if (isCreateMode) {
      setOpenCreateEditModal(true);
    }
  }, [isCreateMode]);

  return (
    <Stack sx={{ height: '100%' }} gap={isSxScreen ? '1rem' : ''}>
      <PageHeaderLayout headerName={'Assessments'}>
        <CustomIconSwitch
          sx={{
            marginRight: 'auto',
          }}
          checked={viewType === globalEnums.EAssessmentsViewType.TABLE}
          onChange={handleToggleView}
          checkedIcon={<TableViewOutlinedIcon />}
          uncheckedIcon={<ViewCarouselOutlinedIcon />}
        />

        <Button
          variant='contained'
          color='primary'
          startIcon={<AddOutlined />}
          fullWidth={isSxScreen}
          onClick={handleCreateAssessment}
        >
          {'Create Assessment'}
        </Button>
      </PageHeaderLayout>

      {viewType === globalEnums.EAssessmentsViewType.RIBBON ? (
        <Suspense fallback={<Loader fullscreen />}>
          <LazyAssessmentsRibbonView
            setOpenCreateEditModal={setOpenCreateEditModal}
            setOpenDeleteModal={setOpenDeleteModal}
            setOpenAdminToolsModal={setOpenAdminToolsModal}
            setOpenScoreUpdateModal={setOpenScoreUpdateModal}
          />
        </Suspense>
      ) : (
        <Suspense fallback={<Loader fullscreen />}>
          <LazyAssessmentsTableView
            isDeleting={isDeleting}
            setOpenCreateEditModal={setOpenCreateEditModal}
            setOpenDeleteModal={setOpenDeleteModal}
            setOpenAdminToolsModal={setOpenAdminToolsModal}
            setOpenScoreUpdateModal={setOpenScoreUpdateModal}
          />
        </Suspense>
      )}

      {openCreateEditModal && (
        <CreateEditModal
          isOpen={openCreateEditModal}
          onClose={() => setOpenCreateEditModal(false)}
          isCreateMode={isCreateMode}
          setIsCreateMode={setIsCreateMode}
        />
      )}

      {openDeleteModal && (
        <DeletePopup
          keyId='delete-assessment'
          title='Delete Assessment'
          itemName={selectedAssessment?.name}
          isOpen={openDeleteModal}
          onConfirm={handleDeleteAssessment}
          onClose={() => setOpenDeleteModal(false)}
        />
      )}

      {openAdminToolsModal && (
        <AssessmentAdminModal
          isOpen={openAdminToolsModal}
          onClose={() => setOpenAdminToolsModal(false)}
        />
      )}

      {openScoreUpdateModal && (
        <ScoreUpdateModal
          isOpen={openScoreUpdateModal}
          onClose={() => setOpenScoreUpdateModal(false)}
        />
      )}
    </Stack>
  );
};

export default AssessmentsPage;